/**
 * Module responsible to wrapper the Rudderstack generated-client with transformations and validations on top of that
 * Rudderstack recommends to avoid 'async' functions, so we need to keep it 'sync'
 *
 * TODO:
 *  - We need to keep adding events here as soon as it is needed ie: PDP, Collection, Cart, etc
 *  - Analytic Section: https://app.asana.com/0/1208717489402917/1209705002912140
 */

import 'vite/modulepreload-polyfill';

import { MARKETING_USER_PREFERENCES, TRACK_EVENT_NAMES } from '~/constants/rudderstack.js';
import * as DataDogLogs from '~/lib/datadog/datadog';
import * as RudderTyperClient from '~/lib/rudderstack/generated-client/rudderTyperClient';
import * as RudderstackUtils from '~/lib/rudderstack/rudderstack-utils';
import * as DeviceUtils from '~/utils/device';
import * as UrlUtils from '~/utils/url';
import * as ValidatorUtils from '~/utils/validator';

/**
 * User must provide performance consent 'C0002' to be tracked by Rudderstack
 *
 * @returns {boolean} - Return if the user has performance consent
 */
const _hasPerformanceConsent = () => window.ALLBIRDS.OneTrust.hasPerformanceConsent();

/**
 * Helper method to wrap an analytic call that require a follow up navigation and apply a delay before navigating to the new URL
 *
 * There is an issue on Safari and Apple Iphone (webkit) when an event is fired right just before a navigation occurs,
 * the browser is cancelling the javascript exec and not sending the event.
 *
 * Use this wrapper when:
 *  - You need to send an event before navigating to a new URL outside the website, otherwise try to use 'Query String API'
 *  - 'Query String API' could not be used for any case
 *
 * Default delay is 500 MS
 */
const navigationDelayForIphoneAndSafari = ({
  analyticsFn,
  delayMs = 500,
  openInNewTab = false,
  urlToNavigate,
}) => {
  DataDogLogs.warn('[Allbirds pixel] - Rudderstack Client - navigation delay on iphone or safari', {
    isIphone: DeviceUtils.isAppleIphone,
    isSafari: DeviceUtils.isSafariDesktop,
  });

  // Function to navigate to the URL on current tab or in a new tab
  const navigateFn = () => {
    if (openInNewTab) {
      window.open(urlToNavigate, '_blank');
    } else {
      window.location.href = urlToNavigate;
    }
  };

  // Call the analytics function
  analyticsFn();

  // Delay the navigation for iphone or safari
  if (DeviceUtils.isSafariDesktop || DeviceUtils.isAppleIphone) {
    setTimeout(() => {
      navigateFn();
    }, delayMs);

    // Inmediately navigate to the URL for other browsers
  } else {
    navigateFn();
  }
};

/**
 * Generic event used to track different types of clicks on the website
 * This was to avoid creating a new event for each type of click
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {string} campaignName - Campaign name
 * @property {string} campaignExperienceName - Campaign Experience Name
 * @property {string} campaignVariationId - Campaign Variation ID
 * @property {string} campaignVariationName - Campaign Variation Name
 * @property {string} copy - This is one a lot of other guides tend to use although if we’re A/B testing copy.
 * @property {string} location - Where this CTA lives, the component or experience that it is part of. (cart, story card, etc.), constants on TRACK_GENERIC_CTA_CLICKED_EVENT.LOCATIONS
 * @property {string} name - The name of this specific CTA that will uniquely define it within it’s location. ie on Cart payment methods, the name would be 'paypal', 'amazonpay', etc.
 * @property {string} type - What type of action this CTA is. Most often this will be a button or link. (button, link, dropdown, etc.), constants on TRACK_GENERIC_CTA_CLICKED_EVENT.TYPES
 * @property {string} url - If this CTA has a URL it links to, include it.
 * @property {string} value - Any numerical information that is important to this CTA? We don’t use this often but there are several cases in our GA events.
 *
 * @returns {void}
 *
 */
const callToActionClicked = ({
  campaignExperienceName,
  campaignName,
  campaignVariationId,
  campaignVariationName,
  copy,
  location,
  name,
  type,
  url,
  value,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(name) || ValidatorUtils.isNil(type) || ValidatorUtils.isNil(location)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - callToActionClicked - Missing required fields',
      {
        location,
        name,
        type,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    campaign_name: campaignName,
    copy,
    experience_name: campaignExperienceName,
    location,
    name,
    type,
    url,
    value,
    variation_id: campaignVariationId,
    variation_name: campaignVariationName,
  };

  RudderTyperClient.callToActionClicked(payload);
};

/**
 * Event used to track 'Call To Action Clicked' when User clicks on a call to action feature link
 * Use QueryString API method if there is a navigation, Iphone and Safari will abort the HTTP call if there is a navigation
 * The track call will be triggered on the next page load, it is needed to send all the data as query string
 * If a base URL is provided, it will respect existing query string and append the new one for tracking
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * Rudderstack Query String API:
 * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * @param {Object} object with several properties
 * @property {string} baseUrl - Base URL to be used for the query string, could contain query string
 * @property {string} campaignName - Campaign name
 * @property {string} campaignExperienceName - Campaign Experience Name
 * @property {string} campaignVariationId - Campaign Variation ID
 * @property {string} campaignVariationName - Campaign Variation Name
 * @property {string} copy - This is one a lot of other guides tend to use although if we’re A/B testing copy.
 * @property {string} location - Where this CTA lives, the component or experience that it is part of. (cart, story card, etc.), constants on TRACK_GENERIC_CTA_CLICKED_EVENT.LOCATIONS
 * @property {string} name - The name of this specific CTA that will uniquely define it within it’s location. ie on Cart payment methods, the name would be 'paypal', 'amazonpay', etc.
 * @property {string} type - What type of action this CTA is. Most often this will be a button or link. (button, link, dropdown, etc.), constants on TRACK_GENERIC_CTA_CLICKED_EVENT.TYPES
 * @property {string} url - If this CTA has a URL it links to, include it.
 * @property {string} value - Any numerical information that is important to this CTA? We don’t use this often but there are several cases in our GA events.
 *
 * @returns {string} - The URL link with the query string API for tracking or undefined if there is an error
 */
const callToActionClickedQueryStringUrlBuilder = ({
  baseUrl = window.location.href,
  campaignExperienceName,
  campaignName,
  campaignVariationId,
  campaignVariationName,
  copy,
  location,
  name,
  type,
  url,
  value,
}) => {
  // Required fields from tracking plan, just log an error and let the query string API build the URL
  if (ValidatorUtils.isNil(name) || ValidatorUtils.isNil(type) || ValidatorUtils.isNil(location)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - callToActionClickedQueryStringUrlBuilder - Missing required fields',
      {
        location,
        name,
        type,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );
  }

  const payload = {
    campaign_name: campaignName,
    copy,
    experience_name: campaignExperienceName,
    location,
    name,
    type,
    url,
    value,
    variation_id: campaignVariationId,
    variation_name: campaignVariationName,
  };

  let urlResult = new URL(baseUrl);

  RudderstackUtils.appendEventAndPropsToQueryStringUrl({
    eventName: TRACK_EVENT_NAMES.CALL_TO_ACTION_CLICKED,
    eventProperties: payload,
    urlObjRef: urlResult,
  });

  return urlResult.toString();
};

/**
 * Event used to update the user marketing preference
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * Warkning: Default values are aligned with downstream systems, be careful when changing them
 *
 * @param {Object} object with several properties
 * @property {string} cmsProductId - CMS Product ID
 * @property {string} messageMedium - Message Medium
 * @property {string} messageType - Message Type
 * @property {boolean} optInStatus - Opt In Status
 * @property {string} placement - Placement
 * @property {string} purpose - Purpose
 * @property {string} sku - SKU
 *
 * @returns {void}
 */
const userMarketingPreference = ({
  cmsProductId = null,
  messageMedium = MARKETING_USER_PREFERENCES.MEDIUM.EMAIL,
  messageType = MARKETING_USER_PREFERENCES.TYPE.ALL,
  optInStatus = false,
  placement = null,
  purpose = null,
  sku = null,
}) => {
  // Required fields from tracking plan
  if (
    ValidatorUtils.isNil(messageMedium) ||
    ValidatorUtils.isNil(messageType) ||
    ValidatorUtils.isNil(optInStatus) ||
    ValidatorUtils.isNil(placement)
  ) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - userMarketingPreference - Missing required fields',
      {
        messageMedium,
        messageType,
        optInStatus,
        placement,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Type validation
  if (typeof optInStatus !== 'boolean') {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - userMarketingPreference - optInStatus must be a boolean',
      {
        optInStatus,
      },
      new Error('Rudderstack Client - optInStatus must be a boolean'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    cms_product_id: cmsProductId,
    message_medium: messageMedium,
    message_type: messageType,
    opt_in_status: optInStatus,
    placement,
    purpose,
    sku,
  };

  RudderTyperClient.userMarketingPreference(payload);
};

/**
 * Event used to track clicks on the homepage hero
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @returns {void}
 *
 */
const homepageHeroClick = ({ heroImageName, linkCTAName, linkDestination }) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(linkDestination)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - homepageHeroClick - Missing required fields',
      {
        linkDestination,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    image_name: heroImageName,
    link_CTA_name: linkCTAName,
    link_destination: linkDestination,
  };

  RudderTyperClient.homepageHeroClicked(payload);
};

/**
 * Event used to track clicks on the homepage hero using query string API
 * Use this method if there is a navigation, Iphone and Safari will abort the HTTP call if there is a navigation
 * The track call will be triggered on the next page load, it is needed to send all the data as query string
 * If a base URL is provided, it will respect existing query string and append the new one for tracking
 *
 * Rudderstack Query String API:
 * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {string} baseUrl - Base URL to be used for the query string, could contain query string
 * @property {string} heroImageName - Hero image name
 * @property {string} linkCTAName - Link CTA name
 * @property {string} linkDestination - Link destination
 *
 * @returns {string} - The URL link with the query string API for tracking
 *
 */
const homepageHeroClickQueryStringUrlBuilder = ({
  baseUrl = window.location.href,
  heroImageName,
  linkCTAName,
  linkDestination,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(linkDestination)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - homepageHeroClickQueryStringUrlBuilder - Missing required fields',
      {
        linkDestination,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return baseUrl;
  }

  if (!UrlUtils.canParse(baseUrl)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - homepageHeroClickQueryStringUrlBuilder - baseUrl can not be parsed',
      {
        baseUrl,
      },
      new Error('Rudderstack Client - baseUrl can not be parsed'),
    );

    return baseUrl;
  }

  let urlResult = new URL(baseUrl);

  // Append the query string to the URL
  RudderstackUtils.appendEventAndPropsToQueryStringUrl({
    eventName: TRACK_EVENT_NAMES.HOMEPAGE_HERO_CLICKED,
    eventProperties: {
      image_name: heroImageName,
      link_CTA_name: linkCTAName,
      link_destination: linkDestination,
    },
    urlObjRef: urlResult,
  });

  return urlResult.toString();
};

/**

 * Event used to track clicks on the homepage modules or sections
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @returns {void}
 *
 */
const homepageModuleClick = ({
  carouselCardName,
  carouselCardPosition,
  carouselCategoryName,
  imageName,
  linkCTAName,
  linkDestination,
  moduleName,
  modulePosition,
  moduleType,
}) => {
  // Required fields from tracking plan
  if (
    ValidatorUtils.isNil(moduleType) ||
    ValidatorUtils.isNil(moduleName) ||
    ValidatorUtils.isNil(modulePosition)
  ) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - homepageModuleClick - Missing required fields',
      {
        moduleName,
        modulePosition,
        moduleType,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    carousel_card_name: carouselCardName,
    carousel_card_position: carouselCardPosition,
    carousel_category_name: carouselCategoryName,
    image_name: imageName,
    link_CTA_name: linkCTAName,
    link_destination: linkDestination,
    module_name: moduleName,
    module_position: modulePosition,
    module_type: moduleType,
  };

  RudderTyperClient.homepageModuleClicked(payload);
};

/**
 * Event used to track products search initiated
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {string} searchEntry - Search entry
 * @property {string} searchSource - Search source
 */
const productsSearchInitiated = ({ searchEntry, searchSource }) => {
  // No validation implemented, same as site 1.0, event is triggered even if some fields are missing

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    search_entry: searchEntry,
    search_source: searchSource,
  };

  RudderTyperClient.productsSearchInitiated(payload);
};

/**
 * Event used to track products search initiated
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {string} query - The search query
 * @property {string} searchEntry - The search entry
 * @property {string} searchSource - The search source
 * @property {number} searchResultsCount - The number of search results
 */

const productsSearched = ({ query, searchEntry, searchResultsCount, searchSource }) => {
  // No validation implemented, same as site 1.0, event is triggered even if some fields are missing

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    number_of_search_results: searchResultsCount,
    query,
    search_entry: searchEntry,
    search_source: searchSource,
  };

  RudderTyperClient.productsSearched(payload);
};

/**
 * Event used to track clicks on the homepage section/module using query string API
 * Use this method if there is a navigation, Iphone and Safari will abort the HTTP call if there is a navigation
 * The track call will be triggered on the next page load, it is needed to send all the data as query string
 * If a base URL is provided, it will respect existing query string and append the new one for tracking
 *
 * Rudderstack Query String API:
 * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *  - 'module_type' and 'card_category_name' are deprecated for site 2.0
 *
 * @param {Object} object with several properties
 * @property {String} baseUrl - Base URL to be used for the query string, could contain query string
 * @property {string} carouselCardName - Carousel card name
 * @property {string} carouselCardPosition - Carousel card position
 * @property {string} imageName - Image name
 * @property {string} linkCTAName - Link CTA name
 * @property {string} linkDestination - Link destination
 * @property {string} moduleName - Module name
 * @property {string} modulePosition - Module position
 *
 * @returns {string} - The URL link with the query string API for tracking or undefined if there is an error
 *
 */
const homepageModuleClickQueryStringUrlBuilder = ({
  baseUrl = window.location.href,
  carouselCardName = null,
  carouselCardPosition = null,
  imageName = null,
  linkCTAName = null,
  linkDestination = null,
  moduleName,
  modulePosition,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(moduleName) || ValidatorUtils.isNil(modulePosition)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - homepageModuleClickQueryStringUrlBuilder - Missing required fields',
      {
        moduleName,
        modulePosition,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return undefined;
  }

  if (!UrlUtils.canParse(baseUrl)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - homepageModuleClickQueryStringUrlBuilder - baseUrl can not be parsed',
      {
        baseUrl,
      },
      new Error('baseUrl can not be parsed'),
    );

    return undefined;
  }

  let urlResult = new URL(baseUrl);

  // Append the query string to the URL
  RudderstackUtils.appendEventAndPropsToQueryStringUrl({
    eventName: TRACK_EVENT_NAMES.HOMEPAGE_MODULE_CLICKED,
    eventProperties: {
      carousel_card_name: carouselCardName,
      carousel_card_position: carouselCardPosition,
      image_name: imageName,
      link_CTA_name: linkCTAName,
      link_destination: linkDestination,
      module_name: moduleName,
      module_position: modulePosition,
    },
    urlObjRef: urlResult,
  });

  return urlResult.toString();
};

/**
 * Event used to track clicks on the footer links
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {string} footerLinkCategory - Footer link category
 * @property {string} footerLinkName - Footer link name
 *
 * @returns {void}
 */
const footerLinkClicked = ({ footerLinkCategory, footerLinkName }) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(footerLinkCategory) || ValidatorUtils.isNil(footerLinkName)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - footerLinkClicked - Missing required fields',
      {
        footerLinkCategory,
        footerLinkName,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    footer_link_category: footerLinkCategory,
    footer_link_name: footerLinkName,
  };

  RudderTyperClient.footerLinkClicked(payload);
};

/**
 * Event used to track 'Footer Link Clicked' when User clicks on a footer link
 * Use QueryString API method if there is a navigation, Iphone and Safari will abort the HTTP call if there is a navigation
 * The track call will be triggered on the next page load, it is needed to send all the data as query string
 * If a base URL is provided, it will respect existing query string and append the new one for tracking
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * Rudderstack Query String API:
 * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * @param {Object} object with several properties
 * @property {string} baseUrl - Base URL to be used for the query string, could contain query string
 * @property {string} footerLinkCategory - Footer link category
 * @property {string} footerLinkName - Footer link name
 *
 * @returns {string} - The URL link with the query string API for tracking or undefined if there is an error
 */
const footerLinkClickedQueryStringUrlBuilder = ({
  baseUrl = window.location.href,
  footerLinkCategory,
  footerLinkName,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(footerLinkCategory) || ValidatorUtils.isNil(footerLinkName)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - footerLinkClickedQueryStringUrlBuilder - Missing required fields',
      { footerLinkCategory, footerLinkName },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  const payload = {
    footer_link_category: footerLinkCategory,
    footer_link_name: footerLinkName,
  };

  let urlResult = new URL(baseUrl);

  RudderstackUtils.appendEventAndPropsToQueryStringUrl({
    eventName: TRACK_EVENT_NAMES.FOOTER_LINK_CLICKED,
    eventProperties: payload,
    urlObjRef: urlResult,
  });

  return urlResult.toString();
};

/**
 * Event used to track when User clicks on a product image to open the zoom modal
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {number} pdpImagePosition - The position/index of the image clicked (0-based)
 * @property {Object} shopifyProduct - Allbirds product representation object, check 'utils.js-product-object.liquid'
 *
 * @returns {void}
 */
const productImageClicked = ({ pdpImagePosition, shopifyProduct }) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(pdpImagePosition) || ValidatorUtils.isNil(shopifyProduct)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productImageClicked - Missing required fields',
      { pdpImagePosition, shopifyProduct },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = RudderstackUtils.getProductImageFormatted({
    imagePosition: pdpImagePosition,
    shopifyProduct,
  });

  RudderTyperClient.productImageClicked(payload);
};

/**
 * Event used to track when User clicks on a product image to open the zoom modal
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {number} pdpImagePosition - The position/index of the image clicked (0-based)
 * @property {Object} shopifyProduct - Allbirds product representation object, check 'utils.js-product-object.liquid'
 *
 * @returns {void}
 */
const productImageScroll = ({ pdpImagePosition, shopifyProduct }) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(pdpImagePosition) || ValidatorUtils.isNil(shopifyProduct)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productImageScroll - Missing required fields',
      { pdpImagePosition, shopifyProduct },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = RudderstackUtils.getProductImageFormatted({
    imagePosition: pdpImagePosition,
    shopifyProduct,
  });

  RudderTyperClient.productImageScroll(payload);
};

/**
 * Event used to identify the user
 * This event also send the user default traits used across the website ie: anonymousId, session_id, dynamic_yield_server_id, etc
 *
 * Native event from Rudderstack, not on Tracking Plan
 *
 * @param {Object} object with several properties
 * @property {Object} additionalTraits - Additional traits to be added to the user
 * @property {Object} apiOptions - API options to be passed to Rudderstack
 * @property {string} email - User email
 * @property {boolean} forceIdentify - Bypass the check to call identify the user
 * @returns {void}
 *
 */
const identifyUser = ({ additionalTraits, apiOptions, email, forceIdentify = false }) => {
  // Consent check
  if (!_hasPerformanceConsent()) return;

  const proposedUserTraits = RudderstackUtils.getUserTraits({ additionalTraits, email });
  const existingUserTraits = RudderstackUtils.getCurrentUserTraits();
  const shouldUpdateUser = RudderstackUtils.shouldUpdateUser(
    existingUserTraits,
    proposedUserTraits,
    email,
  );

  if (shouldUpdateUser || forceIdentify) {
    const userId = proposedUserTraits?.hashed_email || window?.rudderanalytics?.getUserId() || '';

    window.rudderanalytics.identify(userId, proposedUserTraits, apiOptions);
  } else {
    DataDogLogs.warn('[Allbirds pixel] - Rudderstack Client - User already identified');
  }
};

/**
 * Event used to track 'Mini Navigation Clicked' when User clicks on a mini navigation feature link
 * Use QueryString API method if there is a navigation, Iphone and Safari will abort the HTTP call if there is a navigation
 * The track call will be triggered on the next page load, it is needed to send all the data as query string
 * If a base URL is provided, it will respect existing query string and append the new one for tracking
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * Rudderstack Query String API:
 * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * @param {Object} object with several properties
 * @property {string} baseUrl - Base URL to be used for the query string, could contain query string
 * @property {string} linkCTAName - Link CTA name
 * @property {string} linkDestination - Link destination
 *
 * @returns {string} - The URL link with the query string API for tracking or undefined if there is an error
 */
const miniNavigationClickedQueryStringUrlBuilder = ({
  baseUrl = window.location.href,
  linkCTAName,
  linkDestination,
}) => {
  // No validation implemented, same as site 1.0, event is triggered even if some fields are missing

  const payload = {
    link_CTA_name: linkCTAName,
    link_destination: linkDestination,
  };

  let urlResult = new URL(baseUrl);

  RudderstackUtils.appendEventAndPropsToQueryStringUrl({
    eventName: TRACK_EVENT_NAMES.MINI_NAVIGATION_CLICKED,
    eventProperties: payload,
    urlObjRef: urlResult,
  });

  return urlResult.toString();
};

/**
 * Event used to track clicks on the Nav Menu bar
 * Use this method when there is no navigation after the click
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {string} navigationLevel1 - Navigation level 1
 * @property {string} navigationLevel2 - Navigation level 2
 * @property {string} navigationLevel3 - Navigation level 3
 * @property {string} navigationSource - Navigation source
 * @property {string} navigationType - Navigation type
 *
 * @returns {void}
 */
const navigationClicked = ({
  navigationLevel1 = null,
  navigationLevel2 = null,
  navigationLevel3 = null,
  navigationSource = null,
  navigationType,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(navigationType)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - navigationClicked - Missing required fields',
      {
        navigationType,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    navigation_level_1: navigationLevel1,
    navigation_level_2: navigationLevel2,
    navigation_level_3: navigationLevel3,
    navigation_source: navigationSource,
    navigation_type: navigationType,
  };

  RudderTyperClient.navigationClicked(payload);
};

/**
 * Event used to track clicks on the homepage section/module using query string API
 * Use this method if there is a navigation, Iphone and Safari will abort the HTTP call if there is a navigation
 * The track call will be triggered on the next page load, it is needed to send all the data as query string
 * If a base URL is provided, it will respect existing query string and append the new one for tracking
 *
 * Rudderstack Query String API:
 * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *  - 'module_type' and 'card_category_name' are deprecated for site 2.0
 *
 * @param {Object} object with several properties
 * @property {string} navigationLevel1 - Navigation level 1
 * @property {string} navigationLevel2 - Navigation level 2
 * @property {string} navigationLevel3 - Navigation level 3
 * @property {string} navigationSource - Navigation source
 * @property {string} navigationType - Navigation type
 *
 * @returns {string} - The URL link with the query string API for tracking or undefined if there is an error
 *
 */
const navigationClickedQueryStringUrlBuilder = ({
  baseUrl = window.location.href,
  navigationLevel1 = null,
  navigationLevel2 = null,
  navigationLevel3 = null,
  navigationSource = null,
  navigationType,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(navigationType)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - navigationClickedQueryStringUrlBuilder - Missing required fields',
      {
        navigationType,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  if (!UrlUtils.canParse(baseUrl)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - navigationClickedQueryStringUrlBuilder - baseUrl can not be parsed',
      {
        baseUrl,
      },
      new Error('Rudderstack Client - baseUrl can not be parsed'),
    );

    return undefined;
  }

  let urlResult = new URL(baseUrl);

  // Append the query string to the URL
  RudderstackUtils.appendEventAndPropsToQueryStringUrl({
    eventName: TRACK_EVENT_NAMES.NAVIGATION_CLICKED,
    eventProperties: {
      navigation_level_1: navigationLevel1,
      navigation_level_2: navigationLevel2,
      navigation_level_3: navigationLevel3,
      navigation_source: navigationSource,
      navigation_type: navigationType,
    },
    urlObjRef: urlResult,
  });

  return urlResult.toString();
};

/**
 * Event used to track clicks on the Nav Menu bar when User hovers over the menu
 * Use this method when there is no navigation
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {string} navigationLevel1 - Navigation level 1
 * @property {string} navigationLevel2 - Navigation level 2
 * @property {string} navigationLevel3 - Navigation level 3
 * @property {string} navigationSource - Navigation source
 * @property {string} navigationType - Navigation type
 *
 * @returns {void}
 */
const navigationViewed = ({
  navigationLevel1 = null,
  navigationLevel2 = null,
  navigationLevel3 = null,
  navigationSource = null,
  navigationType,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(navigationType)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - navigationViewed - Missing required fields',
      {
        navigationType,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    navigation_level_1: navigationLevel1,
    navigation_level_2: navigationLevel2,
    navigation_level_3: navigationLevel3,
    navigation_source: navigationSource,
    navigation_type: navigationType,
  };

  RudderTyperClient.navigationViewed(payload);
};

/**
 * Page event used to track page views
 * Starting on Rudderstack V3, the default page call is not sent automatically
 *
 * Native event from Rudderstack, not on Tracking Plan
 *
 * @param {string} category
 * @param {string} name
 * @param {Object} properties
 * @param {Object} apiOptions
 * @param {Function} callback
 * @returns {void}
 */
const page = (category, name, properties, apiOptions, callback) => {
  if (!_hasPerformanceConsent()) return;

  window.rudderanalytics?.page(category, name, properties, apiOptions, callback);
};

/**
 * Event used to track product views when User visit the product page or select a variant size
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 *
 * @param {Object} object with several properties
 * @property {Object} product - Allbirds product representation object, check 'utils.js-product-object.liquid'
 * @property {string} sizeSelected - Size selected by the user
 *
 * @returns {void}
 */
const productViewed = ({ product, sizeSelected }) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(product) || !product.handle || (!product.contentful_id && !product.id)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productViewed - Missing required fields',
      {
        product,
        productContentfulId: product?.contentful_id,
        productHandle: product?.handle,
        productShopifyId: product?.id,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const formattedProduct = RudderstackUtils.getProductFormated({ product, sizeSelected });

  const payload = {
    ...formattedProduct,
    currency: RudderstackUtils.getShopCurrency(),
    currency_symbol: RudderstackUtils.getShopCurrencySymbol(),
  };

  RudderTyperClient.productViewed(payload);
};

/**
 * Event used to track product list views when User visit a collection page
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 *
 * @param {Object} param
 * @property {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 *
 * @returns {void}
 */
const productListViewed = ({ collection }) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(collection)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productListViewed - Missing required fields',
      { collection },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const formattedCollection = RudderstackUtils.getCollectionFormatted({
    collection,
    includeProducts: true,
  });

  const payload = {
    ...formattedCollection,
    currency: RudderstackUtils.getShopCurrency(),
    currency_symbol: RudderstackUtils.getShopCurrencySymbol(),
  };

  RudderTyperClient.productListViewed(payload);
};

/**
 *
 * Event used to track when User clicks on a Product Card on a Collection page
 * Use QueryString API method if there is a navigation, Iphone and Safari will abort the HTTP call if there is a navigation
 * The track call will be triggered on the next page load, it is needed to send all the data as query string
 * If a base URL is provided, it will respect existing query string and append the new one for tracking
 * This event was renamed on Amplitud as 'Product List Clicked'
 *
 * Rudderstack Query String API:
 * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 *
 * @param {Object} param Object with several properties
 * @property {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 * @property {string} productClickedId - Product ID
 * @property {string} productClickedPosition - Product position on the grid
 *
 * @returns {string} - The URL link with the query string API for tracking or undefined if there is an error
 *
 */
const productListClickedQueryStringUrlBuilder = ({
  baseUrl = window.location.href,
  collection,
  productClickedId,
  productClickedPosition,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(collection) || ValidatorUtils.isNil(productClickedId)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productListClickedQueryStringUrlBuilder - Missing required fields',
      { collection, productClickedId },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Find the clicked product on the current collection page object
  const productClickedRaw = collection.products.find(
    (product) => product?.id?.toString() == productClickedId,
  );

  // Create a new collection object with the clicked product
  const collectionWithClickedProduct = {
    ...collection,
    products: [{ ...productClickedRaw }],
  };

  // Format the collection object
  const formattedCollection = RudderstackUtils.getCollectionFormatted({
    collection: collectionWithClickedProduct,
    includeProducts: true,
  });

  // Add the product card position to the payload, there should be only one product on the payload
  formattedCollection.products = formattedCollection.products.map((product) => ({
    ...product,
    position: productClickedPosition,
  }));

  const payload = {
    currency: RudderstackUtils.getShopCurrency(),
    currency_symbol: RudderstackUtils.getShopCurrencySymbol(),
    ...formattedCollection,
  };

  let urlResult = new URL(baseUrl);

  RudderstackUtils.appendEventAndPropsToQueryStringUrl({
    eventName: TRACK_EVENT_NAMES.PRODUCT_LIST_CLICKED,
    eventProperties: payload,
    urlObjRef: urlResult,
  });

  return urlResult.toString();
};

/**
 * Event used to track when User applies filters on Collection page
 * - Filters are applied on Collections pages using Shopify theme 2.0 approach
 * - The list of products is udpated automatically by Shopify after applying the filters
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} param Object with several properties
 * @property {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 *
 * @returns {void}
 */
const productListFiltered = ({ collection }) => {
  //  Required fields from tracking plan
  if (ValidatorUtils.isNil(collection)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productListFiltered - Missing required fields',
      { collection },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  // Format collection, this will include filtered products
  const formattedCollection = RudderstackUtils.getCollectionFormatted({
    collection,
    includeProducts: true,
  });

  // Format filters
  const formattedFilters = RudderstackUtils.getCollectionFiltersFormated(collection);

  const payload = {
    currency: RudderstackUtils.getShopCurrency(),
    currency_symbol: RudderstackUtils.getShopCurrencySymbol(),
    ...formattedCollection,
    filters: formattedFilters,
  };

  RudderTyperClient.productListFiltered(payload);
};

/**
 * Event used to track when User clicks on gender toggle on Collection page or the sorting feature
 * Use QueryString API method if there is a navigation, Iphone and Safari will abort the HTTP call if there is a navigation
 * The track call will be triggered on the next page load, it is needed to send all the data as query string
 * If a base URL is provided, it will respect existing query string and append the new one for tracking
 *
 * The collection data should be colected form the current Collection page and Products should not be included
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * Rudderstack Query String API:
 * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * @param {Object} param - Object with several properties
 * @property {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 * @property {string} featureCTA - Feature CTA ie: 'WOMEN', 'MEN'
 * @property {string} featureLinkDestination - Feature link destination ie: 'https://www.allbirds.com/collections/women'
 * @property {string} featureType - Feature type ie: 'pill toggle', 'sorting'
 *
 * @returns {string} - The URL link with the query string API for tracking or undefined if there is an error
 */
const productListFeatureClickedQueryStringUrlBuilder = ({
  baseUrl = window.location.href,
  collection,
  featureCTA,
  featureLinkDestination,
  featureType,
}) => {
  //  Required fields from tracking plan
  if (ValidatorUtils.isNil(collection) || ValidatorUtils.isNil(featureType)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productListFeatureClickedQueryStringUrlBuilder - Missing required fields',
      { collection, featureType },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Format collection, this will include filtered products
  const formattedCollection = RudderstackUtils.getCollectionFormatted({
    collection,
    includeProducts: false,
  });

  const payload = {
    currency: RudderstackUtils.getShopCurrency(),
    currency_symbol: RudderstackUtils.getShopCurrencySymbol(),
    feature_cta: featureCTA?.toUpperCase(),
    feature_link_destination: featureLinkDestination,
    feature_type: featureType,
    ...formattedCollection,
  };

  let urlResult = new URL(baseUrl);

  RudderstackUtils.appendEventAndPropsToQueryStringUrl({
    eventName: TRACK_EVENT_NAMES.PRODUCT_LIST_FEATURE_CLICKED,
    eventProperties: payload,
    urlObjRef: urlResult,
  });

  return urlResult.toString();
};

/**
 * Event used to track clicks on the homepage hero
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @returns {void}
 *
 */
const productListFeatureClicked = ({
  collection,
  featureCTA,
  featureLinkDestination,
  featureType,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(collection) || ValidatorUtils.isNil(featureType)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productListFeatureClicked - Missing required fields',
      {
        collection,
        featureCTA,
        featureLinkDestination,
        featureType,
      },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const formattedCollection = RudderstackUtils.getCollectionFormatted({
    collection,
    includeProducts: false,
  });

  const payload = {
    currency: RudderstackUtils.getShopCurrency(),
    currency_symbol: RudderstackUtils.getShopCurrencySymbol(),
    feature_cta: featureCTA?.toUpperCase(),
    feature_link_destination: featureLinkDestination,
    feature_type: featureType,
    ...formattedCollection,
  };

  RudderTyperClient.productListFeatureClicked(payload);
};

/**
 * Event used to track Cart Viewed when User makes visible the cart drawer
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 *
 * @param {Object} object with several properties
 * @property {Array} ajaxCartItems - List of cart items from AJAX Cart API https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
 *
 * @returns {void}
 */
const cartViewed = ({ ajaxCartItems }) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(ajaxCartItems)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - cartViewed - Missing required fields',
      { ajaxCartItems },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const formattedProducts = RudderstackUtils.getAjaxApiCartItemsFormatted(ajaxCartItems);

  const payload = {
    currency: RudderstackUtils.getShopCurrency(),
    currency_symbol: RudderstackUtils.getShopCurrencySymbol(),
    products: formattedProducts,
  };

  RudderTyperClient.cartViewed(payload);
};

/**
 * Event used to track 'Product Added' when User add a product to the Cart
 *
 * This can be called from different sources, and depending on the source there are different properties to be provided
 *  ie: PDP and Collection page will have a Shopify product representation
 *      Homepage, Pages, will have an AJAX Cart API product representation
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {Array} ajaxCartItems - List of cart items on the Cart from AJAX Cart API, ref https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
 * @property {Object} productAddedShopify - Allbirds product representation object, check 'utils.js-product-object.liquid'
 * @property {Object} productAddedAjaxCart - AJAX Cart API product representation, ref https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
 * @property {number} productAddedQuantity - Quantity of the product added to the cart
 * @property {string} productAddedSource - Source of the product added to the cart
 * @property {string} productSizeSelected - Size selected by the user
 * @returns {void}
 */
const productAdded = ({
  ajaxCartItems,
  productAddedAjaxCart,
  productAddedQuantity = 1,
  productAddedShopify,
  productAddedSource = 'not_source_provided',
  productSizeSelected,
}) => {
  // Required fields from tracking plan
  if (
    // Items on the cart are required and should be an array
    !Array.isArray(ajaxCartItems) ||
    // Size selected should be provided
    ValidatorUtils.isNil(productSizeSelected) ||
    // Product added should be provided from either Shopify or AJAX Cart API
    (ValidatorUtils.isNil(productAddedShopify) && ValidatorUtils.isNil(productAddedAjaxCart))
  ) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productAdded - Missing required fields',
      { ajaxCartItems, productAddedAjaxCart, productAddedShopify, productSizeSelected },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const formattedProducts = RudderstackUtils.getAjaxApiCartItemsFormatted(ajaxCartItems);

  let addedProduct = {};
  // Transform shopify product if present
  if (productAddedShopify) {
    const selectedSizeFormatted = RudderstackUtils.getNormalizedSize(productSizeSelected);
    addedProduct = RudderstackUtils.getProductFormated({
      product: { ...productAddedShopify, quantity: productAddedQuantity },
      sizeSelected: selectedSizeFormatted,
    });
    // Transform AJAX Cart API product if present
  } else {
    addedProduct = RudderstackUtils.getAjaxApiCartItemsFormatted([
      { ...productAddedAjaxCart, quantity: productAddedQuantity },
    ])[0];
  }

  const payload = {
    ...addedProduct,
    cart_added_trigger_source: productAddedSource,
    currency: RudderstackUtils.getShopCurrency(),
    currency_symbol: RudderstackUtils.getShopCurrencySymbol(),
    products: formattedProducts,
  };

  RudderTyperClient.productAdded(payload);
};

/**
 * Event used to track 'Product Removed' when User remove a product from the Cart
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {Array} ajaxCartItems - List of cart items on the Cart from AJAX Cart API, ref https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
 * @property {Object} productRemovedAjaxCart - AJAX Cart API product representation, ref https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
 * @property {number} productRemovedQuantity - Quantity of the product added to the cart
 *
 * @returns {void}
 */
const productRemoved = ({ ajaxCartItems, productRemovedAjaxCart, productRemovedQuantity = 1 }) => {
  // Required fields from tracking plan
  if (
    // Items on the cart are required and should be an array
    !Array.isArray(ajaxCartItems) ||
    // Product removed should be provided
    ValidatorUtils.isNil(productRemovedAjaxCart)
  ) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - productRemoved - Missing required fields',
      { ajaxCartItems, productRemovedAjaxCart, productRemovedQuantity },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  // Format cart items
  const formattedProducts = RudderstackUtils.getAjaxApiCartItemsFormatted(ajaxCartItems);

  // Format removed product
  const removedProduct = RudderstackUtils.getAjaxApiCartItemsFormatted([
    { ...productRemovedAjaxCart, quantity: productRemovedQuantity },
  ])[0];

  const payload = {
    ...removedProduct,
    currency: RudderstackUtils.getShopCurrency(),
    currency_symbol: RudderstackUtils.getShopCurrencySymbol(),
    products: formattedProducts,
  };

  RudderTyperClient.productRemoved(payload);
};

/**
 * Event used to track 'Global Banner Link Clicked' when User clicks on a global banner
 * Use QueryString API method if there is a navigation, Iphone and Safari will abort the HTTP call if there is a navigation
 * The track call will be triggered on the next page load, it is needed to send all the data as query string
 * If a base URL is provided, it will respect existing query string and append the new one for tracking
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * Rudderstack Query String API:
 * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * @param {Object} object with several properties
 * @property {string} baseUrl - Base URL to be used for the query string, could contain query string
 * @property {string} globalBannerName - Name / Text of the global banner
 * @property {string} globalHeaderSource - Url path where banner was clicked from
 *
 * @returns {string} - The URL link with the query string API for tracking or undefined if there is an error
 */
const globalBannerClickedQueryStringUrlBuilder = ({
  baseUrl = window.location.href,
  globalBannerName,
  globalHeaderSource,
}) => {
  // Required fields from tracking plan
  if (ValidatorUtils.isNil(globalBannerName) || ValidatorUtils.isNil(globalHeaderSource)) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack Client - globalBannerClicked - Missing required fields',
      { globalBannerName, globalHeaderSource },
      new Error('Rudderstack Client - Missing required fields'),
    );

    return;
  }

  const payload = {
    global_banner_name: globalBannerName,
    global_header_source: globalHeaderSource,
  };

  let urlResult = new URL(baseUrl);

  RudderstackUtils.appendEventAndPropsToQueryStringUrl({
    eventName: TRACK_EVENT_NAMES.GLOBAL_BANNER_LINK_CLICKED,
    eventProperties: payload,
    urlObjRef: urlResult,
  });

  return urlResult.toString();
};

/**
 * Event used to track 'Experiment Viewed' event
 * This event is triggered after fetching DY campaign data
 *
 * Properties defined on Tracking Plan:
 * https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} object with several properties
 * @property {string} campaignId - Campaign ID
 * @property {string} campaignName - Campaign Name
 * @property {string} experienceId - Experience ID
 * @property {string} experienceName - Experience Name
 * @property {string} experimentType - Experiment Type
 * @property {string} variationId - Variation ID
 * @property {string} variationName - Variation Name
 *
 * @returns {void}
 */
const experimentViewed = ({
  campaignId,
  campaignName,
  experienceId,
  experienceName,
  experimentType,
  variationId,
  variationName,
}) => {
  // No validation implemented, same as site 1.0, event is triggered even if some fields are missing

  // Consent check
  if (!_hasPerformanceConsent()) return;

  RudderTyperClient.setRudderTyperOptions({
    analytics: window.rudderanalytics,
  });

  const payload = {
    campaign_id: campaignId,
    campaign_name: campaignName,
    experience_id: experienceId?.toString(), // convert to string as required by rudderstack
    experience_name: experienceName,
    experiment_type: experimentType,
    variation_id: variationId?.toString(), // convert to string as required by rudderstack
    variation_name: variationName,
    web_platform: 'liquid',
  };

  RudderTyperClient.experimentViewed(payload);
};

export {
  callToActionClicked,
  callToActionClickedQueryStringUrlBuilder,
  cartViewed,
  experimentViewed,
  footerLinkClicked,
  footerLinkClickedQueryStringUrlBuilder,
  globalBannerClickedQueryStringUrlBuilder,
  homepageHeroClick,
  homepageHeroClickQueryStringUrlBuilder,
  homepageModuleClick,
  homepageModuleClickQueryStringUrlBuilder,
  identifyUser,
  miniNavigationClickedQueryStringUrlBuilder,
  navigationClicked,
  navigationClickedQueryStringUrlBuilder,
  navigationDelayForIphoneAndSafari,
  navigationViewed,
  page,
  productAdded,
  productImageClicked,
  productImageScroll,
  productListClickedQueryStringUrlBuilder,
  productListFeatureClicked,
  productListFeatureClickedQueryStringUrlBuilder,
  productListFiltered,
  productListViewed,
  productRemoved,
  productsSearched,
  productsSearchInitiated,
  productViewed,
  userMarketingPreference,
};
